/**
 * <size-dropdown>
 * A custom, accessible size picker that shows the variant price on the right.
 * It drives a hidden native <select> (the source of truth) so Dawn's
 * variant-selects pipeline (price/availability/URL/media updates) is untouched.
 *
 * Note: variant-selects is re-rendered by product-info.js on every variant
 * change, so this element is reinstantiated with fresh prices + selected state.
 * The element therefore stays stateless across changes — connectedCallback wires
 * everything up, disconnectedCallback tears the document listener back down.
 */
if (!customElements.get('size-dropdown')) {
  class SizeDropdown extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onOptionClick = this.onOptionClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-size-trigger]');
      this.menu = this.querySelector('[data-size-menu]');
      this.native = this.querySelector('[data-size-native]');
      this.options = Array.from(this.querySelectorAll('.size-dropdown__option'));

      if (!this.trigger || !this.menu || !this.native) return;

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.options.forEach((option) => option.addEventListener('click', this.onOptionClick));
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      // Auto-switch the size to the first available one when the color changes.
      this.matrix = this.parseMatrix();
      this.variantSelects = this.closest('variant-selects');
      if (this.matrix && this.variantSelects) {
        this.onColorChangeCapture = this.onColorChangeCapture.bind(this);
        // Capture phase: runs BEFORE variant-selects' own (bubble-phase) change
        // handler reads selectedOptionValues, so the corrected size is included
        // in the single re-render fetch — no double request.
        this.variantSelects.addEventListener('change', this.onColorChangeCapture, true);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.variantSelects && this.onColorChangeCapture) {
        this.variantSelects.removeEventListener('change', this.onColorChangeCapture, true);
      }
    }

    parseMatrix() {
      const el = this.querySelector('[data-size-matrix]');
      if (!el) return null;
      try {
        const data = JSON.parse(el.textContent);
        if (data.colorIndex < 0 || data.sizeIndex < 0) return null;
        const map = {};
        data.variants.forEach((v) => {
          if (!map[v.c]) map[v.c] = {};
          map[v.c][v.s] = !!v.a;
        });
        return { map, sizeOrder: data.sizeOrder || [], colorName: data.colorName || '' };
      } catch (error) {
        return null;
      }
    }

    onColorChangeCapture(event) {
      const target = event.target;
      // Only react to the color radio group (size changes are a <select>).
      if (!(target.tagName === 'INPUT' && target.type === 'radio')) return;
      if (this.matrix.colorName && target.name && !target.name.startsWith(this.matrix.colorName + '-')) {
        return;
      }

      const colorMap = this.matrix.map[target.value];
      if (!colorMap) return;

      const currentSize = this.native.value;
      if (colorMap[currentSize] === true) return; // current size still valid — leave it.

      const firstAvailable = this.matrix.sizeOrder.find((size) => colorMap[size] === true);
      if (!firstAvailable) return; // color fully sold out (disabled server-side).

      // Pre-set the native <select> before variant-selects reads the selection.
      Array.from(this.native.options).forEach((option) => {
        option.toggleAttribute('selected', option.value === firstAvailable);
      });
      this.native.value = firstAvailable;
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.menu.hidden = false;
      const target = this.menu.querySelector('.size-dropdown__option.is-selected') || this.options[0];
      if (target) target.focus();
    }

    close(focusTrigger) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.menu.hidden = true;
      if (focusTrigger) this.trigger.focus();
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.isOpen ? this.close() : this.open();
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onOptionClick(event) {
      const option = event.currentTarget;

      // Sold-out with Back in Stock disabled: inert row. Block selection
      // (covers keyboard Enter, which fires click() past CSS pointer-events).
      if (option.getAttribute('aria-disabled') === 'true') return;

      // Sold-out size with Back in Stock enabled: open the notify popup for
      // that exact variant instead of selecting an unbuyable variant. The
      // page's current selection is left untouched.
      if (option.classList.contains('is-notify') && this.openBackInStock(option)) {
        this.close(false); // focus moves into the modal, not back to the trigger
        return;
      }

      this.select(option.getAttribute('data-size-value'), option);
    }

    openBackInStock(option) {
      const sectionId = this.dataset.section;
      const bisForm = document.querySelector(`back-in-stock-form[data-section-id="${sectionId}"]`);
      if (!bisForm || typeof bisForm.openFor !== 'function') return false;

      const variantId = option.getAttribute('data-variant-id');
      if (!variantId) return false;

      // Prefer the full variant title (e.g. "Blue / Single 60 × 80 in") so the
      // modal shows colour + size; fall back to the size name alone.
      const variantTitle = option.getAttribute('data-variant-title');
      const sizeName = option.querySelector('.size-dropdown__name');
      bisForm.openFor(variantId, variantTitle || (sizeName ? sizeName.textContent.trim() : ''));
      return true;
    }

    /**
     * Optimistically reflect the choice on the trigger, then drive the native
     * <select> and let Dawn re-render the picker with the authoritative state.
     */
    select(value, option) {
      if (option) {
        const name = option.querySelector('.size-dropdown__name');
        const priceGroup = option.querySelector('.size-dropdown__price-group');
        const triggerValue = this.querySelector('[data-size-trigger-value]');
        const triggerPrice = this.querySelector('[data-size-trigger-price-group]');

        if (triggerValue && name) triggerValue.textContent = name.textContent.trim();
        if (triggerPrice && priceGroup) triggerPrice.innerHTML = priceGroup.innerHTML;

        this.options.forEach((other) => {
          other.classList.toggle('is-selected', other === option);
          other.setAttribute('aria-selected', other === option ? 'true' : 'false');
        });
      }

      if (this.native.value !== value) this.native.value = value;
      this.native.dispatchEvent(new Event('change', { bubbles: true }));

      this.close(true);
    }

    onKeydown(event) {
      const { key } = event;

      if (!this.isOpen) {
        if (
          document.activeElement === this.trigger &&
          (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ')
        ) {
          event.preventDefault();
          this.open();
        }
        return;
      }

      const items = this.options;
      const index = items.indexOf(document.activeElement);

      switch (key) {
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'ArrowDown':
          event.preventDefault();
          (items[index + 1] || items[0]).focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          (items[index - 1] || items[items.length - 1]).focus();
          break;
        case 'Home':
          event.preventDefault();
          items[0].focus();
          break;
        case 'End':
          event.preventDefault();
          items[items.length - 1].focus();
          break;
        case 'Enter':
        case ' ':
          if (index >= 0) {
            event.preventDefault();
            items[index].click();
          }
          break;
        case 'Tab':
          this.close();
          break;
      }
    }
  }

  customElements.define('size-dropdown', SizeDropdown);
}
